import { rgba } from 'polished'
import styled, { css } from 'styled-components'
import { minWidth, space, width, OpacityProps, opacity } from 'styled-system'
import { breakpoints } from '../Theme'
import { Span } from '../Typography'
import { linkStyle } from '../Typography/styles'
import { BaseButtonProps } from './Button'

const variants = {
  light: css<BaseButtonProps>`
    box-sizing: border-box;
    background: ${p => p.theme.colors.oldLace};
    color: ${p => p.theme.colors.bigStone};

    ${p =>
      p.status === 'idle' &&
      css`
        &:focus {
          box-shadow: 0 0 3px 2px ${p => rgba(p.theme.colors.oldLaceDark, 0.2)};
          background: ${p => p.theme.colors.oldLaceDark};
        }
        &:hover {
          background: ${p => p.theme.colors.oldLaceDark};
        }
      `}

    ${p =>
      p.status === 'disabled' &&
      css`
        opacity: 0.4;
      `}
  `,
  dark: css<BaseButtonProps>`
    background: ${p => p.theme.colors.bigStone};
    color: #fff;
    ${p =>
      p.status === 'idle' &&
      css`
        &:focus {
          box-shadow: 0 0 3px 2px ${p => rgba(p.theme.colors.bigStone, 0.2)};
          background: ${p => p.theme.colors.bigStoneLight};
        }
        &:hover {
          background: ${p => p.theme.colors.bigStoneLight};
        }
      `}
    ${p =>
      p.status === 'disabled' &&
      css`
        opacity: 0.4;
      `}
  `,
  rolex: css<BaseButtonProps>`
    background: ${p => p.theme.rolex.colors.primary};
    color: #fff;
    ${p =>
      p.status === 'idle' &&
      css`
        &:focus {
          box-shadow: 0 0 3px 2px ${p => rgba(p.theme.rolex.colors.primary, 0.2)};
          background: ${p => p.theme.colors.bigStoneLight};
        }
        &:hover {
          color: ${p => p.theme.rolex.colors.primary};
          background: #fff;
        }
      `}
    ${p =>
      p.status === 'disabled' &&
      css`
        opacity: 0.4;
      `}
  `,
  outline: css<BaseButtonProps>`
    background: #fff;
    border: 1px solid ${p => p.theme.colors.bigStone};
    color: ${p => p.theme.colors.bigStone};
    ${p =>
      p.status === 'idle' &&
      css`
        &:focus {
          color: ${p => p.theme.colors.bigStoneLight};
          border-color: ${p => p.theme.colors.bigStoneLight};
        }
        &:hover {
          border-color: ${p => p.theme.colors.bigStoneLight};
        }
      `}
    ${p =>
      p.status === 'disabled' &&
      css`
        opacity: 0.4;
      `}
  `
}

export const Button = styled.button<BaseButtonProps>`
  ${linkStyle};
  ${p => variants[p.variant]};
  ${space};
  ${minWidth};
  ${width};
  transition: all 0.25s ease-in-out;
  position: relative;
  text-transform: uppercase;
  outline: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 3rem;
  padding: 0 2rem;
  @media screen and (min-width: ${breakpoints.medium}) {
    height: 3.5rem;
    padding: 0 2.5rem;
  }

  ${p =>
    p.status !== 'loading' &&
    css`
      &:disabled {
        cursor: not-allowed;
      }
    `}

  ${p =>
    p.status === 'loading' &&
    css`
      cursor: wait;

      svg {
        width: 1.25rem;
        height: 1.25rem;
      }
    `}

  ${p =>
    !p.status &&
    css`
      &:active {
        transform: scale(0.97);
      }
    `}
`

export const Link = Button.withComponent('a')

export const Content = styled(Span)<OpacityProps>`
  ${opacity};
  align-items: center;
`

export const IconParent = styled(Span)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`
